import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Camera, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import Header from '@/components/Header';
import RMAStepper from '@/components/RMAStepper';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Q1: Event Type - Neutral language
const EVENT_TYPE_OPTIONS = [
  { value: 'wedding_guest', label: 'Matrimonio (Invitato/a)' },
  { value: 'wedding_party', label: 'Matrimonio (Testimone/Cerimonia)' },
  { value: 'formal_ceremony', label: 'Cerimonia formale' },
  { value: 'evening_event', label: 'Evento serale / Gala' },
  { value: 'other', label: 'Altro' }
];

// Q2: Event Status
const EVENT_STATUS_OPTIONS = [
  { value: 'upcoming', label: 'Deve ancora svolgersi' },
  { value: 'passed', label: 'È già passato' },
  { value: 'cancelled', label: 'È stato annullato' }
];

// Q3: Return Reason
const RETURN_REASON_OPTIONS = [
  { value: 'too_small', label: 'Troppo stretto/a' },
  { value: 'too_large', label: 'Troppo largo/a' },
  { value: 'different_fit', label: 'Vestibilità diversa dalle aspettative' },
  { value: 'different_color', label: 'Colore diverso da foto' },
  { value: 'defect', label: 'Difetto del prodotto' },
  { value: 'changed_mind', label: 'Ho cambiato idea' },
  { value: 'other', label: 'Altro motivo' }
];

// Q3a: Defect Type (only if defect)
const DEFECT_TYPE_OPTIONS = [
  { value: 'stitching', label: 'Cucitura difettosa' },
  { value: 'zipper_buttons', label: 'Cerniera / Bottoni' },
  { value: 'fabric', label: 'Difetto tessuto' },
  { value: 'stain', label: 'Macchia' },
  { value: 'other', label: 'Altro' }
];

// Q4: Size Guide (only if size issue)
const SIZE_GUIDE_OPTIONS = [
  { value: 'yes', label: 'Sì' },
  { value: 'no', label: 'No' }
];

// Q5: Try-on Conditions (only if size issue) - Neutral
const TRY_ON_OPTIONS = [
  { value: 'event_shoes', label: 'Scarpe da evento' },
  { value: 'planned_top_or_shirt', label: 'Top/camicia previsti' },
  { value: 'final_underwear_or_accessories', label: 'Intimo/accessori definitivi' },
  { value: 'not_fully_tested', label: 'Non ancora provato completamente' }
];

// Q6: Time to Event
const TIME_TO_EVENT_OPTIONS = [
  { value: 'less_than_7_days', label: 'Meno di 7 giorni' },
  { value: 'between_7_14_days', label: '7-14 giorni' },
  { value: 'more_than_14_days', label: 'Più di 14 giorni' },
  { value: 'no_fixed_date', label: 'Nessuna data fissa' }
];

// Q8: Fit Detail (only if size issue) - Neutral
const FIT_DETAIL_OPTIONS = [
  { value: 'tight_chest_or_bust', label: 'Stretto su petto/busto' },
  { value: 'tight_waist', label: 'Stretto in vita' },
  { value: 'tight_hips', label: 'Stretto sui fianchi' },
  { value: 'tight_shoulders', label: 'Stretto sulle spalle' },
  { value: 'too_long', label: 'Troppo lungo' },
  { value: 'too_short', label: 'Troppo corto' },
  { value: 'fit_ok_not_satisfied', label: 'Vestibilità ok ma non soddisfatto/a' }
];

const RMAStep1 = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [surveyData, setSurveyData] = useState({});
  const [defectPhotos, setDefectPhotos] = useState({});

  useEffect(() => {
    const storedOrder = sessionStorage.getItem('currentOrder');
    const storedItems = sessionStorage.getItem('selectedItems');
    const storedSurvey = sessionStorage.getItem('rmaSurveyData');
    
    if (storedOrder && storedItems) {
      setOrder(JSON.parse(storedOrder));
      const items = JSON.parse(storedItems);
      setSelectedItems(items);
      
      // Initialize survey data for each item
      if (storedSurvey) {
        setSurveyData(JSON.parse(storedSurvey));
      } else {
        const initialData = {};
        items.forEach((item, idx) => {
          initialData[item.unique_id || idx] = {
            q1_event_type: '',
            q2_event_status: '',
            q3_return_reason: '',
            q3a_defect_type: '',
            q4_used_size_guide: '',
            q5_try_on_conditions: [],
            q6_time_to_event: '',
            q7_preferred_solution: '',
            q8_fit_detail: []
          };
        });
        setSurveyData(initialData);
      }
    } else {
      navigate('/');
    }
  }, [navigate]);

  const updateItemSurvey = (itemId, field, value) => {
    setSurveyData(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value
      }
    }));
  };

  const toggleMultiSelect = (itemId, field, option) => {
    setSurveyData(prev => {
      const current = prev[itemId]?.[field] || [];
      const updated = current.includes(option)
        ? current.filter(o => o !== option)
        : [...current, option];
      return {
        ...prev,
        [itemId]: {
          ...prev[itemId],
          [field]: updated
        }
      };
    });
  };

  const handlePhotoUpload = (itemId, event) => {
    const files = Array.from(event.target.files);
    if (files.length + (defectPhotos[itemId]?.length || 0) > 5) {
      toast.error('Massimo 5 foto per articolo');
      return;
    }
    
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    
    setDefectPhotos(prev => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), ...newPhotos]
    }));
  };

  const removePhoto = (itemId, photoIndex) => {
    setDefectPhotos(prev => ({
      ...prev,
      [itemId]: prev[itemId].filter((_, idx) => idx !== photoIndex)
    }));
  };

  const isItemComplete = (itemId) => {
    const data = surveyData[itemId];
    if (!data) return false;
    
    // Base required fields
    if (!data.q1_event_type || !data.q2_event_status || !data.q3_return_reason) return false;
    
    const isDefect = data.q3_return_reason === 'defect';
    const isSizeIssue = ['too_small', 'too_large', 'different_fit'].includes(data.q3_return_reason);
    
    // Defect requires defect type and photos
    if (isDefect) {
      if (!data.q3a_defect_type) return false;
      if (!defectPhotos[itemId] || defectPhotos[itemId].length < 2) return false;
    }
    
    // Size issues require additional questions
    if (isSizeIssue) {
      if (!data.q4_used_size_guide) return false;
      // Q5 and Q8 are optional multi-select
    }
    
    // Time to event required for all except defect
    if (!isDefect && !data.q6_time_to_event) return false;
    
    // NOTE: Preferred solution moved to Step 2
    
    return true;
  };

  const allItemsComplete = () => {
    return selectedItems.every((item, idx) => isItemComplete(item.unique_id || idx));
  };

  const handleContinue = () => {
    if (!allItemsComplete()) {
      toast.error('Completa tutte le domande obbligatorie per ogni articolo');
      return;
    }
    
    // Save survey data
    sessionStorage.setItem('rmaSurveyData', JSON.stringify(surveyData));
    sessionStorage.setItem('rmaDefectPhotos', JSON.stringify(
      Object.fromEntries(
        Object.entries(defectPhotos).map(([k, v]) => [k, v.map(p => p.preview)])
      )
    ));
    
    // Check if any item has defect
    const hasDefect = selectedItems.some((item, idx) => 
      surveyData[item.unique_id || idx]?.q3_return_reason === 'defect'
    );
    sessionStorage.setItem('rmaHasDefect', hasDefect ? 'true' : 'false');
    
    // Always go to Step 2 where user will choose their solution
    navigate(`/order/${orderNumber}/rma/step2`);
  };

  if (!order) return null;

  return (
    <div className="page-container bg-white">
      <Header showBack backTo={`/order/${orderNumber}/type`} />
      <RMAStepper currentStep={1} />
      
      <main className="flex-1 py-8 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-3xl font-normal tracking-tight mb-2">
              Motivo del Reso
            </h1>
            <p className="text-muted-foreground font-light mb-8">
              Aiutaci a capire meglio la tua situazione per offrirti la soluzione migliore
            </p>
          </motion.div>

          {/* Items Survey */}
          <div className="space-y-8">
            {selectedItems.map((item, itemIndex) => {
              const itemId = item.unique_id || itemIndex;
              const data = surveyData[itemId] || {};
              const complete = isItemComplete(itemId);
              const isDefect = data.q3_return_reason === 'defect';
              const isSizeIssue = ['too_small', 'too_large', 'different_fit'].includes(data.q3_return_reason);
              
              return (
                <motion.div
                  key={itemId}
                  className={cn(
                    "luxury-card overflow-hidden",
                    complete ? "border-emerald-300 bg-emerald-50/30" : "border-border"
                  )}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: itemIndex * 0.1 }}
                >
                  {/* Item Header */}
                  <div className="p-4 bg-secondary/50 border-b border-border flex items-center gap-4">
                    <div className="w-16 h-20 bg-white overflow-hidden flex-shrink-0">
                      {item.image_url && (
                        <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.variant_title}</p>
                      {!order?.is_exchange_order && (
                        <p className="text-sm font-medium mt-1">€{parseFloat(item.price).toFixed(2)}</p>
                      )}
                    </div>
                    {complete && (
                      <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  {/* Survey Questions */}
                  <div className="p-6 space-y-6">
                    {/* Q1: Event Type */}
                    <div>
                      <label className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-3 block">
                        Per quale tipo di evento? <span className="text-red-500">*</span>
                      </label>
                      <div className="flex flex-col gap-2">
                        {EVENT_TYPE_OPTIONS.map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => updateItemSurvey(itemId, 'q1_event_type', opt.value)}
                            className={cn(
                              "px-4 py-3 text-sm border rounded transition-all text-left",
                              data.q1_event_type === opt.value
                                ? "border-primary bg-primary/10 text-primary font-medium"
                                : "border-border hover:border-primary/50"
                            )}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Q2: Event Status */}
                    <div>
                      <label className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-3 block">
                        Stato dell'evento <span className="text-red-500">*</span>
                      </label>
                      <div className="flex flex-col gap-2">
                        {EVENT_STATUS_OPTIONS.map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => updateItemSurvey(itemId, 'q2_event_status', opt.value)}
                            className={cn(
                              "px-4 py-3 text-sm border rounded transition-all text-left",
                              data.q2_event_status === opt.value
                                ? "border-primary bg-primary/10 text-primary font-medium"
                                : "border-border hover:border-primary/50"
                            )}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Q3: Return Reason */}
                    <div>
                      <label className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-3 block">
                        Motivo principale del reso <span className="text-red-500">*</span>
                      </label>
                      <div className="flex flex-col gap-2">
                        {RETURN_REASON_OPTIONS.map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => updateItemSurvey(itemId, 'q3_return_reason', opt.value)}
                            className={cn(
                              "px-4 py-3 text-sm border rounded transition-all text-left",
                              data.q3_return_reason === opt.value
                                ? "border-primary bg-primary/10 text-primary font-medium"
                                : "border-border hover:border-primary/50"
                            )}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* CONDITIONAL: Defect Type & Photos */}
                    <AnimatePresence>
                      {isDefect && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="space-y-6 bg-red-50 p-4 -mx-2 border border-red-200"
                        >
                          <div>
                            <label className="text-xs font-bold tracking-[0.15em] uppercase text-red-700 mb-3 block">
                              Tipo di difetto <span className="text-red-500">*</span>
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {DEFECT_TYPE_OPTIONS.map(opt => (
                                <button
                                  key={opt.value}
                                  onClick={() => updateItemSurvey(itemId, 'q3a_defect_type', opt.value)}
                                  className={cn(
                                    "px-4 py-2 text-sm border rounded transition-all",
                                    data.q3a_defect_type === opt.value
                                      ? "border-red-500 bg-red-100 text-red-700 font-medium"
                                      : "border-red-200 bg-white hover:border-red-300"
                                  )}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Photo Upload */}
                          <div>
                            <label className="text-xs font-bold tracking-[0.15em] uppercase text-red-700 mb-3 block">
                              Foto del difetto <span className="text-red-500">* (minimo 2)</span>
                            </label>
                            <div className="flex flex-wrap gap-3">
                              {(defectPhotos[itemId] || []).map((photo, pIdx) => (
                                <div key={pIdx} className="relative w-20 h-20 bg-white rounded overflow-hidden border border-red-200">
                                  <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                                  <button
                                    onClick={() => removePhoto(itemId, pIdx)}
                                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                              {(defectPhotos[itemId]?.length || 0) < 5 && (
                                <label className="w-20 h-20 border-2 border-dashed border-red-300 rounded flex flex-col items-center justify-center cursor-pointer hover:border-red-400 transition-colors bg-white">
                                  <Camera className="w-6 h-6 text-red-400 mb-1" />
                                  <span className="text-xs text-red-500">Aggiungi</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={(e) => handlePhotoUpload(itemId, e)}
                                    className="hidden"
                                  />
                                </label>
                              )}
                            </div>
                            {(defectPhotos[itemId]?.length || 0) < 2 && (
                              <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                Carica almeno 2 foto del difetto
                              </p>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    
                    {/* CONDITIONAL: Size-related questions */}
                    <AnimatePresence>
                      {isSizeIssue && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="space-y-6 bg-blue-50 p-4 -mx-2 border border-blue-200"
                        >
                          {/* Q4: Size Guide */}
                          <div>
                            <label className="text-xs font-bold tracking-[0.15em] uppercase text-blue-700 mb-3 block">
                              Hai consultato la guida taglie? <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-3">
                              {SIZE_GUIDE_OPTIONS.map(opt => (
                                <button
                                  key={opt.value}
                                  onClick={() => updateItemSurvey(itemId, 'q4_used_size_guide', opt.value)}
                                  className={cn(
                                    "px-6 py-2 text-sm border rounded transition-all",
                                    data.q4_used_size_guide === opt.value
                                      ? "border-blue-500 bg-blue-100 text-blue-700 font-medium"
                                      : "border-blue-200 bg-white hover:border-blue-300"
                                  )}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Q5: Try-on Conditions */}
                          <div>
                            <label className="text-xs font-bold tracking-[0.15em] uppercase text-blue-700 mb-3 block">
                              Come hai provato l'articolo?
                            </label>
                            <div className="flex flex-col gap-2">
                              {TRY_ON_OPTIONS.map(opt => (
                                <button
                                  key={opt.value}
                                  onClick={() => toggleMultiSelect(itemId, 'q5_try_on_conditions', opt.value)}
                                  className={cn(
                                    "px-4 py-3 text-sm border rounded transition-all text-left",
                                    data.q5_try_on_conditions?.includes(opt.value)
                                      ? "border-blue-500 bg-blue-100 text-blue-700 font-medium"
                                      : "border-blue-200 bg-white hover:border-blue-300"
                                  )}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Q8: Fit Detail */}
                          <div>
                            <label className="text-xs font-bold tracking-[0.15em] uppercase text-blue-700 mb-3 block">
                              Dove non va bene?
                            </label>
                            <div className="flex flex-col gap-2">
                              {FIT_DETAIL_OPTIONS.map(opt => (
                                <button
                                  key={opt.value}
                                  onClick={() => toggleMultiSelect(itemId, 'q8_fit_detail', opt.value)}
                                  className={cn(
                                    "px-4 py-3 text-sm border rounded transition-all text-left",
                                    data.q8_fit_detail?.includes(opt.value)
                                      ? "border-blue-500 bg-blue-100 text-blue-700 font-medium"
                                      : "border-blue-200 bg-white hover:border-blue-300"
                                  )}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    
                    {/* Q6: Time to Event (for all except defect) */}
                    <AnimatePresence>
                      {data.q3_return_reason && !isDefect && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                        >
                          <label className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-3 block">
                            Quanto manca all'evento? <span className="text-red-500">*</span>
                          </label>
                          <div className="flex flex-col gap-2">
                            {TIME_TO_EVENT_OPTIONS.map(opt => (
                              <button
                                key={opt.value}
                                onClick={() => updateItemSurvey(itemId, 'q6_time_to_event', opt.value)}
                                className={cn(
                                  "px-4 py-3 text-sm border rounded transition-all text-left",
                                  data.q6_time_to_event === opt.value
                                    ? "border-primary bg-primary/10 text-primary font-medium"
                                    : "border-border hover:border-primary/50"
                                )}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Action Buttons */}
          <motion.div
            className="flex items-center justify-between mt-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Button 
              onClick={() => navigate(`/order/${orderNumber}/type`)}
              variant="outline"
              className="rounded-none"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Indietro
            </Button>
            
            <Button 
              onClick={handleContinue}
              disabled={!allItemsComplete()}
              className="luxury-btn-primary inline-flex items-center gap-3"
              data-testid="rma-step1-continue"
            >
              Continua
              <ArrowRight className="w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default RMAStep1;
